import React, { FC, Fragment, useContext, useState, useEffect } from "react";
import { EventType } from "./types";
import Telemetry from "../../../utils/rudderstackAnalytics";
const noPosterIcon = require("../styles/images/noPoster.svg");
const shareIcon = require("../../../../app/assets/images/share-icon.svg");
const privacyIcon = require("../../../../public/images/icon-change-privacy.svg");
const manageEvent = require("../../../../public/images/icon-manage-event.svg");
const viewAlbums = require("../../../../public/images/icon-view-albums.svg");
const showGalleryIcon = require("../styles/images/ShowGallery.svg");
const eventShortCutIcon = require("../styles/images/event-shortcut.svg");
const manageAccess = require("../styles/images/manage-access.svg");
const editIcon = require("../styles/images/edit.svg");
const submitIcon = require("../../ManagePrepaidCustomers/components/images/submit.svg");
const cancelIcon = require("../../ManagePrepaidCustomers/components/images/cancel.svg");

import moment from "moment";
import { PhotoManagerContext } from ".";
import PublishModeIcon from "./PublishModeIcon";
import { useEditEntityNameInGrid } from "./useEditEntityNameInGrid";
import { updateEventAPI } from "../API/api";

interface EventsGridProps {
  events: EventType[];
  albumLoadingId: number | null;
  showActionTooltip: (id: number | null, title: string, type: string) => void;
  handleEventClickWithNavigation: (event: EventType) => void;
  setActiveShareModalEventId: (id: number) => void;
  setMarkUploadCompletePopup: (id: number) => void;
  setPrivacyPopupId: (id: number) => void;
  source: "root-events" | "nested-events";
}

const EventsGrid: FC<EventsGridProps> = ({
  events,
  albumLoadingId,
  showActionTooltip,
  handleEventClickWithNavigation,
  setActiveShareModalEventId,
  setMarkUploadCompletePopup,
  source,
  setPrivacyPopupId
}) => {
  const context = useContext(PhotoManagerContext);
  const [displayEvents, setDisplayEvents] = useState<EventType[]>(events);

  const parentFolderId =
    source === "nested-events" ? context?.selectedFolderId : null;

  useEffect(() => {
    setDisplayEvents(events);
  }, [events]);

  const {
    entitiesInEditMode,
    entityNameDrafts,
    entitiesInLoadingState,
    toggleEditMode,
    updateDraftName,
    handleKeyDownAction,
    submitNameChange
  } = useEditEntityNameInGrid<EventType>({
    updateAPI: async (event: EventType, newName: string) => {
      setDisplayEvents(prev =>
        prev.map(e => (e.id === event.id ? { ...e, name: newName } : e))
      );

      await updateEventAPI(event.id, { name: newName });
    },
    onSuccess: (event: EventType, newName: string) => {
      if (source === "root-events") {
        context?.optimisticUpdateEventName(event.id, newName);
        context?.reloadEventData("root");
      } else if (source === "nested-events" && parentFolderId) {
        context?.reloadEventData("nested", parentFolderId, "LibraryFolder");
      }
    }
  });

  const ISOToDateTimeFormat = (inputVal: string): string => {
    const date = moment.utc(inputVal, "YYYY-MM-DDTHH:mm:ss");
    return date.format("DD MMM, YYYY");
  };
  const handleGalleryClick = () => {
    if (!context) return;

    if (!context.simpleEventCreationEnabled) {
      const params = new URLSearchParams();
      if (context.selectedFolderId) {
        params.append("library_folder_id", context.selectedFolderId.toString());
      }
      window.location.href = `/dashboard/events/new/our?${params}`;
    } else {
      context.openCreateEventModal();
    }
  };

  return (
    <div className="ws-album-cards-grid">
      {displayEvents.map(event => {
        if (event.demoPreparationInProgress) {
          return (
            <div
              key={event.id}
              className="ws-album-card ws-album-card-preparing"
              aria-busy="true"
            >
              <div className="ws-album-thumbnail ws-album-preparing-thumbnail">
                <span className="fa fa-spinner fa-spin ws-album-preparing-spinner"></span>
                <div className="ws-album-preparing-label">Preparing sample event</div>
              </div>
            </div>
          );
        }
        return (
        <div
          key={event.id}
          className="ws-album-card"
          onClick={() => {
            Telemetry.track("content-library:event-opened", {
              source: "main-panel"
            });
            handleEventClickWithNavigation(event);
          }}
        >
          <div className="ws-album-thumbnail">
            {event.coverPhoto ? (
              <img
                className="ws-album-thumbnail-img"
                src={event.coverPhoto}
                alt={`Album ${event.name}`}
              />
            ) : (
              <div className="ws-no-poster">
                <img
                  src={noPosterIcon}
                  className="ws-no-poster-icon"
                  alt="No poster icon"
                />
                <span>No cover photo</span>
              </div>
            )}
            <span
              className="ws-price-icon"
              id={`price-icon-${event.id}`}
              onClick={e => e.stopPropagation()}
              data-toggle="tooltip"
              data-placement="top"
              onMouseEnter={e =>
                showActionTooltip(event.id, "Price per photo", "price")
              }
            >
              {event.price === 0 ? "Free" : event.displayPrice}
            </span>
            {event.isReference ? (
              <img
                src={eventShortCutIcon}
                alt="short-cut"
                className="shortcut-icon"
              />
            ) : null}
          </div>
          {albumLoadingId === event.id && (
            <div className="ws-image-overlay-loader">
              <div className="ws-loading-spinner">
                <span className="fa fa-spinner fa-spin ws-color-white"></span>
              </div>
            </div>
          )}
          <div className="ws-album-info">
            <div className="ws-album-info-text">
              {entitiesInEditMode.includes(event.id) ? (
                <div
                  className="ws-edit-album-name"
                  data-edit-entity-id={event.id}
                  onClick={e => e.stopPropagation()}
                >
                  <textarea
                    defaultValue={entityNameDrafts[event.id] || event.name}
                    onChange={e => updateDraftName(e, event.id, e.target.value)}
                    onKeyDown={e => handleKeyDownAction(e, event)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                    onFocus={e => {
                      const val = e.target.value;
                      e.target.value = "";
                      e.target.value = val;
                    }}
                  />
                  <div className="ws-edit-event-actions">
                    {entitiesInLoadingState.includes(event.id) ? (
                      <span
                        className="fa fa-spinner fa-spin"
                        style={{ marginRight: 5 }}
                      ></span>
                    ) : (
                      <>
                        <img
                          className="actionIcon"
                          src={submitIcon}
                          onClick={e => submitNameChange(e, event)}
                          style={{ cursor: "pointer", opacity: 1 }}
                        />
                        <img
                          className="actionIcon"
                          style={{
                            marginLeft: 5,
                            cursor: "pointer",
                            opacity: 1
                          }}
                          src={cancelIcon}
                          onClick={e =>
                            toggleEditMode(e, event.id, false, event.name)
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <h3>
                  <span className="ws-album-info-title ws-event-name-with-edit">
                    {event.name}
                    <span className="ws-cardicons">
                      {!event.isReference && event.canRename && (
                        <span
                          className="ws-edit-name-icon"
                          data-toggle="tooltip"
                          data-placement="top"
                          data-container="body"
                          title="Edit name"
                          id={`edit-icon-${event.id}`}
                          onMouseEnter={e =>
                            showActionTooltip(event.id, "Edit Name", "edit")
                          }
                          onClick={e =>
                            toggleEditMode(e, event.id, true, event.name)
                          }
                        >
                          <img
                            src={editIcon}
                            alt="Edit event name"
                            className="ws-edit-icon"
                          />
                        </span>
                      )}
                      <PublishModeIcon
                        event={event}
                        showActionTooltip={showActionTooltip}
                      />
                    </span>
                  </span>
                </h3>
              )}
              <p style={{ margin: 0 }}>
                {ISOToDateTimeFormat(event.startDate)}
              </p>
            </div>

            <div className="ws-album-up-status-wrap">
              <div className="ws-album-up-status">
                {event.uploadCompleteStatus === true ? (
                  <div className="event-up-done">
                    <span className="event-up-check pop-check"></span>
                    <span className="event-up-donetext">Upload complete</span>
                  </div>
                ) : (
                  event.markUploadCompleteURL != null &&
                  event.uploadCompleteStatus != null && (
                    <Fragment>
                      <span
                        className="ws-event-up-link"
                        onClick={e => {
                          e.stopPropagation();
                          setMarkUploadCompletePopup(event.id);
                        }}
                      >
                        Mark upload complete
                      </span>
                      {event.waitingCustomerLeadsCount > 0 && (
                        <div className="ws-event-wait">
                          {event.waitingCustomerLeadsCount === 1 ? (
                            <span>
                              <b>1 person</b> is waiting
                            </span>
                          ) : (
                            <span>
                              <b>{event.waitingCustomerLeadsCount} people</b> are
                              waiting
                            </span>
                          )}{" "}
                        </div>
                      )}
                    </Fragment>
                  )
                )}
              </div>
            </div>
            <div className="ws-actions-div ws-event-icon-row">
              <div className="ws-album-actions ws-album-list-actions">
                {event.canShare && (
                  <span
                    id={`share-icon-${event.id}`}
                    className="ws-action-button view mr-2"
                    onClick={e => {
                      e.stopPropagation();
                      setActiveShareModalEventId(event.id);
                    }}
                    data-toggle="tooltip"
                    data-placement="top"
                    onMouseEnter={e =>
                      showActionTooltip(event.id, "Share Event", "share")
                    }
                  >
                    <img src={shareIcon} alt="Share event" />
                  </span>
                )}
                <div
                  id={`albums-icon-${event.id}`}
                  className="ws-action-button upload"
                  onClick={e => handleEventClickWithNavigation(event)}
                  data-toggle="tooltip"
                  data-placement="top"
                  onMouseEnter={e =>
                    showActionTooltip(event.id, "View Albums", "albums")
                  }
                >
                  <img src={viewAlbums} alt="View albums" />
                </div>
                {event.canUpdatePrivacy && (
                  <span
                    className="ws-action-button view mr-2"
                    onClick={e => {
                      e.stopPropagation();
                      setPrivacyPopupId(event.id);
                    }}
                    id={`privacy-icon-${event.id}`}
                    data-toggle="tooltip"
                    data-placement="top"
                    onMouseEnter={e =>
                      showActionTooltip(event.id, "Update Privacy", "privacy")
                    }
                  >
                    <img src={privacyIcon} alt="privacy icon" />
                  </span>
                )}
                <a
                  id={`manage-icon-${event.id}`}
                  href={
                    event.canAccessManageEventPage
                      ? event.manageEventPath
                      : "javascript:void(0);"
                  }
                  className={`ws-action-button view ${
                    !event.canAccessManageEventPage ? "disabled" : ""
                  }`}
                  onClick={e => {
                    if (!event.canAccessManageEventPage) e.preventDefault(); // Block navigation
                    e.stopPropagation();
                  }}
                  data-toggle="tooltip"
                  data-placement="top"
                  title={
                    event.canAccessManageEventPage
                      ? "Manage Event"
                      : "You don't have access"
                  }
                  onMouseEnter={() =>
                    showActionTooltip(
                      event.id,
                      event.canAccessManageEventPage
                        ? "Manage Event"
                        : "You don't have access",
                      "manage"
                    )
                  }
                >
                  <img src={manageEvent} alt="Manage Event" />
                </a>
                {event.canManageAccess && (
                  <span
                    className="ws-action-button view mr-2"
                    onClick={e => {
                      e.stopPropagation();
                      context?.toggleManagePermissionsModal(
                        true,
                        "Event",
                        event.id,
                        event.name
                      );
                    }}
                    id={`manage-access-icon-${event.id}`}
                    data-toggle="tooltip"
                    data-placement="top"
                    title="Manage Access"
                    onMouseEnter={() =>
                      showActionTooltip(
                        event.id,
                        "Manage Access",
                        "manage-access"
                      )
                    }
                  >
                    <img src={manageAccess} alt="Manage Access" />
                  </span>
                )}
              </div>
              <a
                id={`view-icon-${event.id}`}
                target="_blank"
                href={new URL(event.browsePhotosURL).pathname}
                className="ws-open-gallery-icon"
                onClick={e => e.stopPropagation()}
                data-toggle="tooltip"
                data-placement="top"
                onMouseEnter={() =>
                  showActionTooltip(event.id, "View Event Gallery", "view")
                }
              >
                <img
                  src={showGalleryIcon}
                  alt="Show Photos"
                  style={{ width: 18 }}
                />
              </a>
            </div>
          </div>
        </div>
        );
      })}
      {source === "root-events" && context?.canCreateRootContent && !context?.invitedEventsMode && (
        <div
          className="ws-album-card ws-create-album-card"
          onClick={() => handleGalleryClick()}
        >
          <div className="folder-create-icon-div">
            <div className="folder-create-icon">+</div>
          </div>
          Create Event
        </div>
      )}
      {source === "nested-events" &&
        context?.selectedFolderData?.canCreateEvents && (
          <div
            className="ws-album-card ws-create-album-card"
            onClick={() => handleGalleryClick()}
          >
            <div className="folder-create-icon-div">
              <div className="folder-create-icon">+</div>
            </div>
            Create Event
          </div>
        )}
    </div>
  );
};

export default EventsGrid;
